import { h } from '@omni-torn/ui';
import { LOOT_LEVEL_MINUTES, lootLevel, lootLevelTimes, lootRoman, readLootRangersReply, readNpcLoot, type LootRangersPlan, type NpcLootResponse, type NpcLootTimer } from '@omni-torn/shared';
import { extensionNpcPlan } from '../bridge';
import { server } from '../session';
import { ago, box, duration, ext, kids, loading, problem, profileUrl, table, tct } from '../ui';

/**
 * NPC loot timers (website roadmap S05; TornStats' and torn.report's Loot Timer): each loot NPC with when it left
 * hospital, its loot level now and when it reaches each level — from the Omni-Torn server (`GET /api/npc/loot`, the
 * server reads the NPCs' public profiles with its own key). Loot Rangers' attack time and order sit beside it, only
 * through the extension (it already gets them from lzpt.io); nothing of that goes to the server.
 */

const SERVER_EVERY = 60_000;
const LZPT_EVERY = 3 * 60_000;

const stateText = (n: NpcLootTimer, now: number): Node | string => {
  if (n.state === 'unknown') return h('span', { class: 'muted' }, 'not read yet');
  if (n.state === 'hospital' && n.hospOut && n.hospOut > now) return h('span', { class: 't-red' }, `In hospital — out in ${duration((n.hospOut - now) / 1000)}`);
  if (n.state === 'other') return n.description ?? 'Away';
  return h('span', { class: 't-green' }, 'Out');
};

export async function renderLoot(view: HTMLElement): Promise<void> {
  const body = h('div', {}, loading('Reading the NPC loot timers…'));
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'NPC loot')), body);

  let data: NpcLootResponse | null = null;
  /** Server clock − this browser's clock (countdowns follow the server). */
  let skew = 0;
  let lzpt: LootRangersPlan | null = null;
  let lzptState: 'asking' | 'none' | 'ok' = 'asking';
  const now = () => Date.now() + skew;

  const loadServer = async (): Promise<boolean> => {
    try {
      const d = readNpcLoot(await server<unknown>('GET', '/api/npc/loot'));
      if (!d) throw new Error('The server sent something unexpected.');
      data = d;
      skew = d.now - Date.now();
      return true;
    } catch (err) {
      if (!data) body.replaceChildren(problem(err));
      return false;
    }
  };
  const loadLzpt = async () => {
    const reply = await extensionNpcPlan().catch(() => null);
    lzpt = readLootRangersReply(reply);
    lzptState = reply ? 'ok' : 'none';
  };

  const planBox = h('div', {});
  const tableBox = h('div', {});
  const drawPlan = () => {
    const t = now();
    if (lzptState === 'asking') return planBox.replaceChildren(h('p', { class: 'muted' }, 'Asking the extension for Loot Rangers’ plan…'));
    if (lzptState === 'none')
      return planBox.replaceChildren(h('p', { class: 'muted' }, 'Loot Rangers’ attack time and order show here when the Omni-Torn extension is installed in this browser (it already gets them from ', ext('https://lzpt.io', 'lzpt.io'), ').'));
    if (!lzpt || (!lzpt.attackNow && lzpt.attackAt === null))
      return planBox.replaceChildren(h('p', {}, 'No attack planned right now', lzpt?.reason ? ` — ${lzpt.reason}` : '', '.'));
    const order = lzpt.order.map((n) => n.name).join(' → ');
    planBox.replaceChildren(
      h(
        'p',
        { 'data-lzpt': 'plan' },
        h('strong', {}, lzpt.attackNow ? 'Attack on now' : `Attack at ${tct(lzpt.attackAt!)} TCT`),
        !lzpt.attackNow && lzpt.attackAt! > t ? ` (in ${duration((lzpt.attackAt! - t) / 1000)})` : '',
        order ? ` — order: ${order}` : '',
        lzpt.reason ? ` — ${lzpt.reason}` : '',
      ),
    );
  };
  const drawTable = () => {
    if (!data) return;
    const t = now();
    const lzOrder = (id: number) => (lzpt && (lzpt.attackNow || lzpt.attackAt !== null) ? lzpt.order.findIndex((n) => n.id === id) : -1);
    tableBox.replaceChildren(
      table(
        data.npcs,
        [
          { label: 'NPC', get: (n) => h('span', { 'data-npc': String(n.id) }, ext(profileUrl(n.id), n.name)), sort: (n) => n.name },
          { label: 'Status', get: (n) => stateText(n, t) },
          { label: 'Out of hospital', get: (n) => (n.hospOut ? `${tct(n.hospOut)} TCT` : n.state === 'okay' ? h('span', { class: 'muted', title: 'Seen out of hospital only so far — the time comes the next time it’s in hospital' }, 'not known yet') : '—'), sort: (n) => n.hospOut ?? 0 },
          {
            label: 'Loot level',
            get: (n) => {
              const lv = lootLevel(n, t);
              if (!lv.level) return '—';
              return h('span', {}, h('strong', {}, lootRoman(lv.level)), lv.nextAt ? h('span', { class: 'muted' }, ` · ${lootRoman(lv.level + 1)} in ${duration((lv.nextAt - t) / 1000)}`) : h('span', { class: 'muted' }, ' · max'));
            },
            sort: (n) => lootLevel(n, t).level,
          },
          {
            label: 'Levels (TCT)',
            get: (n) =>
              n.hospOut
                ? h(
                    'span',
                    { class: 'small' },
                    ...lootLevelTimes(n.hospOut).flatMap((at, i) => [i ? ' · ' : '', h('span', { class: at <= t ? 'muted' : '' }, `${lootRoman(i + 1)} ${tct(at)}`)]),
                  )
                : '—',
          },
          {
            label: 'Loot Rangers',
            get: (n) => {
              const i = lzOrder(n.id);
              return i < 0 ? h('span', { class: 'muted' }, '—') : h('span', { 'data-lzpt-order': String(i + 1) }, `#${i + 1}${lzpt!.attackAt && !lzpt!.attackNow ? ` at ${tct(lzpt!.attackAt)}` : lzpt!.attackNow ? ' now' : ''}`);
            },
            sort: (n) => {
              const i = lzOrder(n.id);
              return i < 0 ? 99 : -i;
            },
          },
          { label: 'Read', get: (n) => (n.checkedAt ? h('span', { class: 'muted small' }, ago(n.checkedAt - skew)) : '—'), cls: 'r' },
        ],
        { remember: 'npc-loot' },
      ),
    );
  };

  if (!(await loadServer())) return;
  body.replaceChildren(
    ...kids(
    box('Loot Rangers', planBox, { id: 'lzpt' }),
    box('Loot timers', tableBox, { id: 'npc-loot' }),
    h(
      'p',
      { class: 'muted small' },
      `Times in TCT (Torn time = UTC). Loot levels: I when an NPC leaves hospital, then ${LOOT_LEVEL_MINUTES.slice(1)
        .map((m, i) => `${lootRoman(i + 2)} after ${duration(m * 60)}`)
        .join(', ')}. The Omni-Torn server reads each NPC’s public profile with its own key (about once a minute in all). Loot Rangers’ plan comes from `,
      ext('https://lzpt.io', 'lzpt.io'),
      ' through your extension and isn’t kept by Omni-Torn.',
    ),
    !data!.polling && h('p', { class: 'muted small' }, 'This server has no Torn key of its own, so the times aren’t being updated.'),
    ),
  );
  drawPlan();
  drawTable();
  void loadLzpt().then(() => {
    drawPlan();
    drawTable();
  });

  let lastServer = Date.now();
  let lastLzpt = Date.now();
  const timer = setInterval(() => {
    if (!view.isConnected || !body.isConnected) return clearInterval(timer);
    const jobs: Array<Promise<unknown>> = [];
    if (Date.now() - lastServer >= SERVER_EVERY) {
      lastServer = Date.now();
      jobs.push(loadServer());
    }
    if (Date.now() - lastLzpt >= LZPT_EVERY) {
      lastLzpt = Date.now();
      jobs.push(loadLzpt());
    }
    void Promise.all(jobs).then(() => {
      drawPlan();
      drawTable();
    });
  }, 15_000);
}
